import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, Plus, Trash2, Key, Copy, AlertCircle, ExternalLink } from 'lucide-react';
import { useUser } from "@/context/user-provider";
import { apiKeyService } from "@/services/api-key-service";

export function ApiKeyManagement() {
  const { apiKeys, refreshApiKeys, isApiKeysLoading } = useUser();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Extract service ID from API key metadata
  const getServiceIdFromMetadata = (metadata: any): string | null => {
    if (!metadata) return null;
    try {
      const meta = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      return meta?.serviceId || null;
    } catch {
      return null;
    }
  };

  // New API Key state - only local UI state
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKeyExpiresIn, setNewApiKeyExpiresIn] = useState<string>('');
  const [newApiKeyPermissions, setNewApiKeyPermissions] = useState<string>('');
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [showGeneratedApiKey, setShowGeneratedApiKey] = useState(false);

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newApiKeyName.trim()) {
      setError('API Key name is required');
      return;
    }

    try {
      // Prepare API key creation data
      const apiKeyData: any = {
        name: newApiKeyName.trim(),
      };

      if (newApiKeyExpiresIn) {
        apiKeyData.expiresIn = parseInt(newApiKeyExpiresIn); // Already in seconds
      }

      if (newApiKeyPermissions) {
        try {
          apiKeyData.permissions = JSON.parse(newApiKeyPermissions);
        } catch (parseError) {
          setError('Permissions must be valid JSON');
          return;
        }
      }

      const result = await apiKeyService.create(apiKeyData);

      // Store the generated API key - this is the only time we'll see it
      setGeneratedApiKey(result.key || null);
      setNewApiKeyName('');
      setNewApiKeyExpiresIn('');
      setNewApiKeyPermissions('');

      setSuccess('API key created successfully');

      // Refresh API keys from provider
      await refreshApiKeys();
    } catch (err: any) {
      console.error('Error creating API key:', err);
      setError(err.message || 'Failed to create API key');
    }
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    try {
      await apiKeyService.delete(apiKeyId);
      setSuccess('API key deleted successfully');

      // Refresh API keys from provider
      await refreshApiKeys();
    } catch (err: any) {
      console.error('Error deleting API key:', err);
      setError(err.message || 'Failed to delete API key');
    }
  };

  const handleCopyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setSuccess('API key copied to clipboard');
    }).catch(() => {
      setError('Failed to copy API key to clipboard');
    });
  };

  const handleVerifyApiKey = async (apiKey: string) => {
    try {
      const result = await apiKeyService.verify(apiKey);

      if (result.valid) {
        setSuccess('API key is valid and active');
      } else {
        setSuccess('API key is not valid');
      }
    } catch (err: any) {
      console.error('Error verifying API key:', err);
      setError(err.message || 'Failed to verify API key');
    }
  };

  if (isApiKeysLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">API Key Management</h1>
        <p className="text-muted-foreground">Manage your API keys for programmatic access</p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Dev Warning - Current API Key Limitations */}
      <Alert variant="destructive" className="mb-6 border-amber-500 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-900">
          <strong>⚠️ Development Notice:</strong> API Keys currently accept raw JSON permissions but are not bound to any services yet.
          To create service-scoped API keys, use the <strong>Access tab</strong> in the Service Detail page.
          Future improvements will allow restricting API keys to specific service endpoints and actions from the service manifest.
        </AlertDescription>
      </Alert>

      <div className="space-y-6">
        {/* Create API Key Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Create New API Key
            </CardTitle>
            <CardDescription>Create a new API key for programmatic access</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateApiKey} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="apiKeyName">API Key Name</Label>
                <Input
                  id="apiKeyName"
                  value={newApiKeyName}
                  onChange={(e) => setNewApiKeyName(e.target.value)}
                  placeholder="e.g., Production Server, Personal Laptop"
                />
                <p className="text-sm text-muted-foreground">
                  A descriptive name for this API key
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiKeyExpiry">Expiry (Optional, in seconds)</Label>
                <Input
                  id="apiKeyExpiry"
                  type="number"
                  value={newApiKeyExpiresIn}
                  onChange={(e) => setNewApiKeyExpiresIn(e.target.value)}
                  placeholder="e.g., 3600 (1 hour), 2592000 (30 days)"
                />
                <p className="text-sm text-muted-foreground">
                  Maximum expiry is 1 year (31536000 seconds)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiKeyPermissions">Permissions (Optional, JSON format)</Label>
                <Input
                  id="apiKeyPermissions"
                  value={newApiKeyPermissions}
                  onChange={(e) => setNewApiKeyPermissions(e.target.value)}
                  placeholder='e.g., {"files": ["read", "write"], "users": ["read"]}'
                />
                <p className="text-sm text-muted-foreground">
                  Define what actions this key can perform (e.g., {"{ \"files\": [\"read\", \"write\"] }"})
                </p>
              </div>

              <Button type="submit" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Create API Key
              </Button>
            </form>

            {generatedApiKey && (
              <div className="mt-6 p-4 bg-blue-50 rounded-md">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-blue-900">Your New API Key</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Make sure to copy this key now. You won't be able to see it again!
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowGeneratedApiKey(!showGeneratedApiKey)}
                  >
                    {showGeneratedApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 p-3 bg-blue-100 rounded-md font-mono text-sm break-all">
                    {showGeneratedApiKey ? generatedApiKey : '••••••••••••••••••••••••••••••••••••••••••••'}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleCopyApiKey(generatedApiKey!)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>

                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVerifyApiKey(generatedApiKey!)}
                  >
                    Verify Key
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setGeneratedApiKey(null);
                      setSuccess('Generated key cleared from view');
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* API Keys List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Your API Keys
            </CardTitle>
            <CardDescription>Manage your existing API keys</CardDescription>
          </CardHeader>
          <CardContent>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-1">No API Keys</h3>
                <p className="text-muted-foreground">
                  You have not created any API keys yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((apiKey) => (
                  <div
                    key={apiKey.id}
                    className="flex items-center justify-between p-4 border rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="font-medium">
                        {apiKey.name || `API Key ${apiKey.id.slice(0, 8)}...`}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Created: {new Date(apiKey.createdAt).toLocaleDateString()}
                        {apiKey.expiresAt && ` | Expires: ${new Date(apiKey.expiresAt).toLocaleDateString()}`}
                        {apiKey.start && ` | Starts with: ${apiKey.start}`}
                      </div>
                      {apiKey.permissions && (
                        <div className="text-xs mt-1">
                          <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                            Permissions: {typeof apiKey.permissions === 'string' ? apiKey.permissions : JSON.stringify(apiKey.permissions)}
                          </span>
                        </div>
                      )}
                      {getServiceIdFromMetadata(apiKey.metadata) && (
                        <div className="text-xs mt-2">
                          <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Service-scoped Key
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {!apiKey.enabled && (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                          Disabled
                        </span>
                      )}
                      {getServiceIdFromMetadata(apiKey.metadata) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const serviceId = getServiceIdFromMetadata(apiKey.metadata);
                            if (serviceId) {
                              navigate(`/console/services/${serviceId}`);
                            }
                          }}
                          title="Go to service management page"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteApiKey(apiKey.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
